import {
    date,
    FormattedStreamData,
    FoundColumn,
    milliseconds,
    ShapeName,
    StreamDataColumn
} from '@squaredup/data-streams';
import { formatTickAsDate } from 'dashboard-engine/util/tickDateFormatter';
import { formatTickAsColumn, getDecimalPlaces } from 'dashboard-engine/util/tickFormatter';
import { useMemo } from 'react';

/**
 * Returns a function that formats a value in the chart. Used for labels on the bars
 */
export const useValueFormatter = (valueColumn: FoundColumn, data: FormattedStreamData) =>
    useMemo(
        () =>
            formatTickAsColumn(
                valueColumn.column,
                {
                    data,
                    valueColumn: valueColumn.column
                },
                // prevent truncating of value like axes have as bar can be wider.
                { maxLength: 50 }
            ),
        [data, valueColumn]
    );

/**
 * Returns a function that formats values as ticks
 */
export const getTickFormatter = (data: FormattedStreamData, valueColumn: FoundColumn, xAxisColumnShape: ShapeName) => {
    return (col: StreamDataColumn) => {
        const formatter = formatTickAsColumn(
            col,
            {
                data,
                valueColumn: valueColumn.column
            },
            {
                maxLength: Infinity // Truncation is handled in the YAxis and XAxis components
            }
        );

        return (value: unknown) => {
            if (typeof value !== 'number') {
                return formatter(value);
            }

            if (xAxisColumnShape === milliseconds.name) {
                return formatter(value, {
                    formatDuration: false,
                    decimalPlaces: getDecimalPlaces(value)
                });
            }

            return formatter(value, {
                decimalPlaces: getDecimalPlaces(value)
            });
        };
    };
};

export const useAxisFormatters = ({
    data,
    valueColumn,
    xAxisColumn,
    yAxisColumn,
    categories
}: {
    data: FormattedStreamData;
    valueColumn: FoundColumn;
    xAxisColumn: StreamDataColumn;
    yAxisColumn: StreamDataColumn;
    categories: string[];
}) => {
    const formatColumnTick = useMemo(
        () => getTickFormatter(data, valueColumn, xAxisColumn.shapeName),
        [data, valueColumn, xAxisColumn]
    );

    const xAxisTickFormatter = useMemo(() => formatColumnTick(xAxisColumn), [xAxisColumn, formatColumnTick]);

    const yAxisTickFormatter = useMemo(
        () => (yAxisColumn.shapeName === date.name ? formatTickAsDate(categories) : formatColumnTick(yAxisColumn)),
        [yAxisColumn, categories, formatColumnTick]
    );

    return {
        xAxisTickFormatter,
        yAxisTickFormatter
    };
};
